import React, { useEffect, useMemo, useContext } from 'react'
import { toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Button, Form, Input, Table, Upload } from 'antd'
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons'
import { postMsg } from 'src/components'
import { appStore } from 'src/stores'
import { $consts } from 'src/plugins'
import { getApiURL } from 'src/utils/axios'
import { getTemplateConfig, downloadTemplate } from './helper'
import { set } from 'lodash'
import 'src/styles/components/base/templateImport.less'

type State = {
  step: number,
  setStep: (step: number) => void,
  config: any,
  updateConfig: (data: any) => void,
  response: any,
  setResponse: (data: any) => void,
}

const ImportContext = React.createContext<State>({
  step: 0,
  setStep () { },
  config: {},
  updateConfig () { },
  response: {},
  setResponse () { }
})

const ImportResult = observer(() => {
  const state = useContext(ImportContext)

  const getColumns = (columns: any[]) => {
    const validColumns = columns.filter((col: any) => !col.Nav_EditColumn || col.Nav_EditColumn.IS_DEFAULT)
    const presetColumns = validColumns.length ? [
      {
        title: '导入结果',
        dataIndex: 'DataCheckFlag',
        width: 100,
        render: (text: boolean) => !text ? '成功' : <span className='color-error'>失败</span>
      },
      {
        title: '失败原因',
        dataIndex: 'DataCheckInfo',
        render: (text: string) => text || <span>--<span className='opacity-0'>失败原因</span></span>
      }
    ] : []

    return presetColumns.concat(validColumns.map((col: any) => {
      const { FieldName, Label, Nav_EditColumn: Nav } = col
      return {
        title: Label,
        dataIndex: FieldName.split('.'),
        ellipsis: true,
        render: (text: any) => {
          switch (Nav?.CONTROL_TYPE) {
            // 枚举下拉单选框
            case $consts['TABLE/CONTROL_TYPE_ENUM_SELECT']:
              Nav.ENUM && (text = appStore.getEnumLabel(Nav.ENUM, text))
              break
            // 单选 Switch 控件
            // 复选框控件
            case $consts['TABLE/CONTROL_TYPE_SWITCH']:
            case $consts['TABLE/CONTROL_TYPE_CHECKBOX']:
              text = text ? '是' : '否'
              break
          }
          return text
        }
      }
    }))
  }

  const columns = useMemo(() => {
    const { Nav_Columns = [] } = state.config.Nav_ImportTable ?? {}
    return getColumns(Nav_Columns)
  }, [state.config.Nav_ImportTable])

  const getExpandable = (subTables: any[]) => {
    if (!subTables.length) return undefined

    return {
      expandedRowRender: (record: any) => {
        return subTables.map((table: any, index: number) => {
          return (
            <Table
              bordered
              key={`${record.ID}_${index}`}
              rowKey='ID'
              size='small'
              pagination={false}
              columns={getColumns(table.Nav_Columns)}
              dataSource={record[table.NavProperty]}
              expandable={getExpandable(table.Nav_SubTables ?? [])}
            />
          )
        })
      }
    }
  }

  const expandable = useMemo(() => {
    const { Nav_SubTables = [] } = state.config.Nav_ImportTable ?? {}
    return getExpandable(Nav_SubTables)
  }, [state.config.Nav_ImportTable])

  const { Data = [], MessageList = [] } = state.response
  return (
    <div className='template-import__item'>
      <p className='template-import__item-title'>导入结果</p>
      <Table
        rowKey='ID'
        size='small'
        pagination={false}
        columns={columns}
        dataSource={Data}
        scroll={{ x: 'max-content', y: Data.length ? 'calc(100vh - 577px)' : undefined }}
        expandable={expandable}
      />
      <div className='template-import__item-result'>
        <p>提示信息</p>
        {MessageList.map((item: string, index: number) => <p key={`${item}_${index}`}>{item}</p>)}
        {!MessageList.length ? <p>暂无</p> : null}
      </div>
    </div>
  )
})

const TemplateInfo = observer(() => {
  const state = useContext(ImportContext)

  const handleDownload = (isData: boolean) => {
    const copyData = toJS(state.config.Nav_ImportConfig)
    set(copyData, 'IsData', isData)
    downloadTemplate(copyData)
  }

  const { headers } = appStore.current
  const upload = {
    name: 'file',
    action: getApiURL(`/${state.config.Nav_ImportConfig?.SERVICE_CODE ?? 'PF'}/Import/ImportAndUpdate`),
    data: { OrgId: headers.OrgId, ImportConfigCode: state.config.Nav_ImportConfig?.CODE, delFlag: false },
    accept: '.xls,.xlsx',
    headers: headers,
    onChange ({ file }: any) {
      const { status, response } = file
      if (status === 'done') {
        if (response.IsSuccessful) {
          postMsg(`${file.name} 已导入`, 'info')
          state.setResponse(response.Data ?? {})
        } else {
          postMsg(`${response.ErrorMessage} `, 'error')
        }
      } else if (status === 'error') {
        if (response?.ErrorMessage) {
          postMsg(`${file.name} 导入错误，详情：${response.ErrorMessage}`, 'error')
        } else {
          postMsg(`${file.name} 导入错误`, 'error')
        }
      }
    },
  }

  return (
    <div className='template-import__item'>
      <p className='template-import__item-title'>模板信息</p>
      <Form layout='inline' className='mg-b20'>
        <Form.Item label='导入列表'>
          <Input disabled value={state.config.Nav_ImportConfig?.NAME} />
        </Form.Item>
        <Form.Item label='模板编码'>
          <Input disabled value={state.config.Nav_ImportConfig?.CODE} />
        </Form.Item>
      </Form>
      <Form layout='inline'>
        <Form.Item label='模板下载'>
          <Button
            type='link'
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(false)}
          >下载模板</Button>
          <Button
            type='link'
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(true)}
          >下载模板（含数据）</Button>
          <Upload {...upload}>
            <Button type='primary' icon={<UploadOutlined />}>选择导入文件</Button>
          </Upload>
        </Form.Item>
      </Form>
    </div>
  )
})

const TemplateImport = (props: { formCode: string }) => {
  const state = useLocalObservable<State>(() => {
    return {
      step: 0,
      setStep (step: number) {
        this.step = step
      },
      config: {},
      updateConfig (data: any) {
        Object.assign(this.config, data)
      },
      response: {},
      setResponse (data: any) {
        this.response = data
      }
    }
  })

  useEffect(() => {
    getTemplateConfig(props.formCode).then((res: any) => {
      res?.IsSuccessful ? state.updateConfig(res.Data) : postMsg('获取配置失败', 'error')
    })
  }, [props.formCode])

  return (
    <ImportContext.Provider value={state}>
      <div className='template-import'>
        <TemplateInfo />
        <ImportResult />
      </div>
    </ImportContext.Provider>
  )
}

export default TemplateImport